<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LineSidebar from '@/components/vuebits/LineSidebar.vue';

const { t } = useI18n();

const sections = computed(() => [
  { id: 'hero', label: t('sectionNav.01') },
  { id: 'product', label: t('sectionNav.02') },
  { id: 'flywheel', label: t('sectionNav.03') },
  { id: 'calculator', label: t('sectionNav.04') },
  { id: 'scenarios', label: t('sectionNav.05') },
  { id: 'plans', label: t('sectionNav.06') },
  { id: 'faq', label: t('sectionNav.07') },
  { id: 'testimonials', label: t('sectionNav.08') },
]);

const navItems = computed(() => sections.value.map((item) => item.label));

const activeId = ref('hero');
const activeIndex = computed(() => {
  const index = sections.value.findIndex((item) => item.id === activeId.value);
  return index >= 0 ? index : 0;
});

const isHidden = ref(true);
const navRef = ref(null);
const isDarkBackground = ref(true);
/** 点击跳转期间锁定高亮，避免 scroll spy 在滚动未完成时回写旧值 */
const pendingScrollId = ref(null);

const accentColor = '#155eef';
const textColor = computed(() => (isDarkBackground.value ? '#94a3b8' : '#64748b'));
const markerColor = computed(() => (isDarkBackground.value ? '#64748b' : '#cbd5e1'));

/** 根据背景亮度判断是否为深色 */
function isDarkColor(color) {
  if (!color || color === 'transparent' || color === 'rgba(0, 0, 0, 0)') return null;

  const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) return null;

  const [, r, g, b] = match.map(Number);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.45;
}

const DARK_SECTION_IDS = new Set(['hero', 'site-footer']);

function resolveDarkBackground(surface) {
  if (DARK_SECTION_IDS.has(surface.id)) return true;

  let el = surface;
  while (el && el instanceof HTMLElement) {
    const tone = isDarkColor(getComputedStyle(el).backgroundColor);
    if (tone !== null) return tone;
    el = el.parentElement;
  }

  return false;
}

function updateBackgroundTone() {
  if (!navRef.value || isHidden.value) return;

  const rect = navRef.value.getBoundingClientRect();
  const x = Math.min(window.innerWidth - 1, Math.max(0, rect.left + rect.width / 2));
  const y = Math.min(window.innerHeight - 1, Math.max(0, rect.top + rect.height / 2));
  const elements = document.elementsFromPoint(x, y);

  const surface = elements
    .find((el) => !navRef.value?.contains(el))
    ?.closest('section, footer');

  if (!surface) return;

  isDarkBackground.value = resolveDarkBackground(surface);
}

function updateNavVisibility() {
  const hero = document.getElementById('hero');
  const footer = document.getElementById('site-footer');
  let hide = false;

  if (hero) {
    hide ||= hero.getBoundingClientRect().bottom > window.innerHeight * 0.45;
  }

  if (footer) {
    hide ||= footer.getBoundingClientRect().top <= window.innerHeight;
  }

  isHidden.value = hide;
}

function getScrollOffset() {
  const padding = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop);
  return Number.isFinite(padding) ? padding : 80;
}

/** 按视口内可见高度判定当前章节 */
function resolveActiveSection() {
  const viewportTop = getScrollOffset();
  const viewportBottom = window.innerHeight;

  let bestId = sections.value[0].id;
  let bestVisible = -1;

  for (const section of sections.value) {
    const el = document.getElementById(section.id);
    if (!el) continue;

    const { top, bottom } = el.getBoundingClientRect();
    const visibleTop = Math.max(top, viewportTop);
    const visibleBottom = Math.min(bottom, viewportBottom);
    const visibleHeight = Math.max(0, visibleBottom - visibleTop);

    if (visibleHeight >= bestVisible) {
      bestVisible = visibleHeight;
      bestId = section.id;
    }
  }

  return bestId;
}

function getSectionScrollTop(id) {
  const el = document.getElementById(id);
  if (!el) return null;

  const offset = getScrollOffset();
  return Math.max(0, el.getBoundingClientRect().top + window.scrollY - offset);
}

function clearScrollLock() {
  pendingScrollId.value = null;
  if (scrollUnlockTimer) {
    clearTimeout(scrollUnlockTimer);
    scrollUnlockTimer = 0;
  }
}

function scheduleScrollUnlock() {
  if (scrollUnlockTimer) clearTimeout(scrollUnlockTimer);

  const checkSettled = () => {
    const targetId = pendingScrollId.value;
    if (!targetId) return;

    const targetTop = getSectionScrollTop(targetId);
    if (targetTop === null) {
      clearScrollLock();
      runScrollWork();
      return;
    }

    if (Math.abs(window.scrollY - targetTop) < 4) {
      clearScrollLock();
      runScrollWork();
      return;
    }

    scrollUnlockTimer = setTimeout(checkSettled, 80);
  };

  scrollUnlockTimer = setTimeout(checkSettled, 80);
}

function scrollTo(id) {
  const top = getSectionScrollTop(id);
  if (top === null) return;

  pendingScrollId.value = id;
  activeId.value = id;
  window.scrollTo({ top, behavior: 'smooth' });
  scheduleScrollUnlock();
}

function onItemClick(index) {
  const section = sections.value[index];
  if (!section) return;
  scrollTo(section.id);
}

function onScroll() {
  if (scrollRafId) return;
  scrollRafId = requestAnimationFrame(runScrollWork);
}

function runScrollWork() {
  scrollRafId = 0;

  const prevActive = activeId.value;
  const prevHidden = isHidden.value;
  let nextActive = prevActive;

  if (pendingScrollId.value) {
    nextActive = pendingScrollId.value;
  } else {
    nextActive = resolveActiveSection();
  }

  activeId.value = nextActive;
  updateNavVisibility();

  if (nextActive !== prevActive || prevHidden !== isHidden.value) {
    updateBackgroundTone();
  }
}

let scrollRafId = 0;
let scrollUnlockTimer = 0;

function onScrollImmediate() {
  runScrollWork();
}

watch(isHidden, (hidden) => {
  if (!hidden) updateBackgroundTone();
});

onMounted(() => {
  onScrollImmediate();
  window.addEventListener('scroll', onScroll, { passive: true });
});

onUnmounted(() => {
  if (scrollRafId) cancelAnimationFrame(scrollRafId);
  clearScrollLock();
  window.removeEventListener('scroll', onScroll);
});
</script>

<template>
  <nav
    ref="navRef"
    class="section-nav fixed right-4 top-1/2 z-40 hidden 2xl:block 2xl:right-6"
    :class="isHidden ? 'section-nav--hidden pointer-events-none' : 'section-nav--visible'"
    :aria-hidden="isHidden"
    :aria-label="t('sectionNav.label')"
  >
    <LineSidebar
      :items="navItems"
      :default-active="activeIndex"
      :accent-color="accentColor"
      :text-color="textColor"
      :marker-color="markerColor"
      marker-side="right"
      :show-index="true"
      :show-marker="true"
      :proximity-radius="90"
      :max-shift="14"
      :marker-length="36"
      :marker-gap="8"
      :tick-scale="0.45"
      :item-gap="18"
      :font-size="0.8125"
      falloff="smooth"
      @item-click="onItemClick"
    />
  </nav>
</template>

<style scoped>
.section-nav {
  transform: translateY(-50%);
  transition:
    opacity 0.5s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}

.section-nav--hidden {
  opacity: 0;
  transform: translate(16px, -50%);
}

.section-nav--visible {
  opacity: 1;
  transform: translate(0, -50%);
}
</style>
